import * as React from "react";
import {
  Box,
  ButtonBase,
  Chip,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { ReactComponent as CustomDropDownIcon } from "../../../assets/icons/black_down_arrow.svg";

interface Rate {
  pk: number;
  capacity: number;
  customer_type: {
    singular: string;
  };
  customer_prototype: {
    total: number; // cents
  };
}

interface CustomerTypeSelectorProps {
  rates: Rate[];
  selectedRate: number;
  quantities: { [key: number]: number };
  handleRateChange: (rate: Rate, quantity: number) => void;
  setQuantities: React.Dispatch<
    React.SetStateAction<{ [key: number]: number }>
  >;
}

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function CustomerTypeSelector({
  rates,
  selectedRate,
  quantities,
  handleRateChange,
  setQuantities,
}: CustomerTypeSelectorProps) {
  const displayRates = React.useMemo(
    () => (rates ?? []).filter((r) => r.capacity > 0),
    [rates],
  );

  const getQty = React.useCallback(
    (ratePk: number) => quantities[ratePk] ?? 1,
    [quantities],
  );

  const setQty = React.useCallback(
    (ratePk: number, qty: number) => {
      setQuantities((prev) => ({ ...prev, [ratePk]: qty }));
    },
    [setQuantities],
  );

  const selectRate = React.useCallback(
    (rate: Rate, qty?: number) => {
      const nextQty = qty ?? getQty(rate.pk);
      setQty(rate.pk, nextQty);
      handleRateChange(rate, nextQty);
    },
    [getQty, handleRateChange, setQty],
  );

  if (displayRates.length === 0) return null;

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {displayRates.map((rate) => {
        const isSelected = rate.pk === selectedRate;
        const qty = getQty(rate.pk);
        const price =
          rate.customer_prototype.total > 0
            ? currency.format(rate.customer_prototype.total / 100)
            : null;

        return (
          <Paper
            key={rate.pk}
            variant="outlined"
            sx={{
              overflow: "hidden",
              borderColor: isSelected ? "success.main" : "divider",
              bgcolor: isSelected ? "success.50" : "background.paper",
              transition:
                "background-color 120ms ease, border-color 120ms ease, transform 120ms ease",
            }}
          >
            <ButtonBase
              onClick={() => {
                // Clicking the row selects this rate.
                // If only 1 available, force qty=1. Otherwise keep current/default qty.
                selectRate(rate, rate.capacity === 1 ? 1 : undefined);
              }}
              sx={{
                width: "100%",
                textAlign: "left",
                display: "block",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1.5,
                  py: 1.25,
                  gap: 1.25,
                }}
              >
                {/* Left */}
                <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {rate.customer_type?.singular}
                    </Typography>

                    <Chip
                      size="small"
                      label={`${rate.capacity} Available`}
                      sx={{
                        height: 22,
                        fontSize: 12,
                        bgcolor: isSelected ? "success.100" : "action.selected",
                        borderRadius: 999,
                      }}
                    />
                  </Stack>

                  {price && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontWeight: 600 }}
                    >
                      {price}
                    </Typography>
                  )}
                </Stack>

                {/* Right */}
                {rate.capacity > 1 && (
                  <Box
                    onClick={(e) => e.stopPropagation()} // don't trigger row select when interacting with dropdown
                    sx={{ flexShrink: 0 }}
                  >
                    <Select
                      size="small"
                      value={qty}
                      onChange={(event: SelectChangeEvent<number>) => {
                        const newQty = Number(event.target.value);
                        setQty(rate.pk, newQty);
                        handleRateChange(rate, newQty);
                      }}
                      IconComponent={CustomDropDownIcon as any}
                      sx={{
                        minWidth: 72,
                        "& .MuiSelect-select": {
                          py: 0.75,
                          pr: 4,
                          fontWeight: 700,
                        },
                        "& fieldset": {
                          borderColor: isSelected ? "success.main" : "divider",
                        },
                        "&:hover fieldset": {
                          borderColor: isSelected
                            ? "success.main"
                            : "text.primary",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: { maxHeight: 280 },
                        },
                      }}
                    >
                      {Array.from(
                        { length: rate.capacity },
                        (_, idx) => idx + 1,
                      ).map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}
              </Stack>
            </ButtonBase>
          </Paper>
        );
      })}
    </Stack>
  );
}

export default CustomerTypeSelector;
